import { predictLabel } from "./predict";
import type { NetworkNode, OrbitLayer } from "./types";

export function layerRole(layer: OrbitLayer): string {
  if (layer === "inner") return "NOW";
  if (layer === "middle") return "NEXT";
  return "LATER";
}

export function layerHint(layer: OrbitLayer): string {
  if (layer === "inner") return "NOW · click to go inside";
  if (layer === "middle") return "NEXT · click to go inside";
  return "LATER · click to go inside";
}

export function shortTitle(raw: string, max = 120): string {
  return predictLabel(raw, max);
}

function domain(topic: string): "club" | "vinyl" | "ai" | "city" | "music" | "generic" {
  const t = topic.toLowerCase();
  if (/\b(club|venue|nightclub|door|rave|after-?hours)\b/.test(t)) return "club";
  if (/\b(vinyl|record|pressing|reissue)\b/.test(t)) return "vinyl";
  if (/\b(ai|djing|stems?|algorithm|booth)\b/.test(t)) return "ai";
  if (/\b(detroit|berlin|lagos|johannesburg|s[ãa]o paulo|seoul|london|accra)\b/.test(t)) return "city";
  if (/\b(music|sound|song|scene|listen)\b/.test(t)) return "music";
  return "generic";
}

type BranchSpec = { name: string; layer: OrbitLayer; role: string };

const SETS: Record<string, BranchSpec[]> = {
  music: [
    { name: "Club music becomes immersive", layer: "inner", role: "Now" },
    { name: "Chart music becomes playlist-led", layer: "inner", role: "Now" },
    { name: "Music becomes paid by tickets and streams", layer: "inner", role: "Now" },
    { name: "Scenes spread room to room", layer: "middle", role: "Next" },
    { name: "Listening becomes clips and spatial audio", layer: "middle", role: "Next" },
    { name: "Hits become playlist-made", layer: "middle", role: "Next" },
    { name: "Listeners leave the algorithm", layer: "outer", role: "Later" },
    { name: "Music becomes bass-heavy and immersive", layer: "outer", role: "Later" },
  ],
  club: [
    { name: "Club music becomes immersive", layer: "inner", role: "Now" },
    { name: "Rooms become the instrument", layer: "inner", role: "Now" },
    { name: "Rooms keep human DJs", layer: "inner", role: "Now" },
    { name: "Nights become a room and a stream", layer: "middle", role: "Next" },
    { name: "Clubs make the hybrid official", layer: "middle", role: "Next" },
    { name: "City rules cut late nights", layer: "outer", role: "Later" },
    { name: "Rooms get priced out of the city", layer: "outer", role: "Later" },
  ],
  vinyl: [
    { name: "Old records become new again", layer: "inner", role: "Now" },
    { name: "DJs still buy the pressing", layer: "inner", role: "Now" },
    { name: "Vinyl becomes a luxury niche", layer: "middle", role: "Next" },
    { name: "Vinyl becomes too costly to press", layer: "outer", role: "Later" },
  ],
  ai: [
    { name: "DJ sets become live stem remixes", layer: "inner", role: "Now" },
    { name: "AI DJs take over warm-up sets", layer: "inner", role: "Now" },
    { name: "Music becomes owned by platforms and labels", layer: "middle", role: "Next" },
    { name: "Rooms keep human DJs", layer: "outer", role: "Later" },
  ],
  city: [
    { name: "Apps miss the real local scenes", layer: "inner", role: "Now" },
    { name: "Radio still names new scenes", layer: "inner", role: "Now" },
    { name: "Scenes spread room to room", layer: "middle", role: "Next" },
    { name: "City rules cut late nights", layer: "outer", role: "Later" },
  ],
  generic: [
    { name: "Club music becomes immersive", layer: "inner", role: "Now" },
    { name: "Hits become playlist-made", layer: "middle", role: "Next" },
    { name: "Music becomes bass-heavy and immersive", layer: "outer", role: "Later" },
  ],
};

const PALETTE = ["#73e0d2", "#f0bd68", "#ca8cff", "#427d68", "#cf9464"];

export function predictiveBranches(topic: string): NetworkNode[] {
  const set = SETS[domain(topic)];
  return set.map((b, i) => ({
    name: b.name,
    metricLabel: b.role,
    orbitLayer: b.layer,
    colorHex: PALETTE[i % PALETTE.length],
    cluster: b.layer === "inner" ? "lineage" : b.layer === "middle" ? "technology" : "behavior",
    signalLabel: b.layer === "inner" ? "FOUNDATIONAL" : b.layer === "middle" ? "EMERGING" : "CONTESTED",
    why: `${b.role} from ${shortTitle(topic)}.`,
    relation: b.layer === "inner" ? "ORIGIN" : b.layer === "middle" ? "MUTATION" : "HORIZON",
  }));
}

export function sanitizeNode(node: NetworkNode, parentTopic: string): NetworkNode {
  const layer: OrbitLayer = node.orbitLayer === "inner" || node.orbitLayer === "middle" || node.orbitLayer === "outer" ? node.orbitLayer : "middle";
  const name = predictLabel(node.name || parentTopic);
  const role = node.metricLabel && /^(Now|Next|Later)\b/.test(node.metricLabel) ? node.metricLabel : layer === "inner" ? "Now" : layer === "middle" ? "Next" : "Later";
  return {
    ...node,
    name,
    metricLabel: role,
    orbitLayer: layer,
  };
}
